import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, todayStr, errMsg, confirmDelete } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { FormDialog } from "@/components/FormDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ExcavationHistory } from "@/components/ExcavationHistory";
import { Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const JOB_STATUSES = ["Planlanıyor", "Aktif", "Beklemede", "Tamamlandı", "İptal"];
export const JOB_FIELDS = [
  { name: "vehicle_id", label: "Araç (Plaka)", type: "entity", endpoint: "/vehicles", labelKey: (v) => `${v.plate} · ${v.brand} ${v.model}` },
  { name: "customer_id", label: "İşi Kime Yaptık? (Müşteri)", type: "entity", endpoint: "/customers", required: true, quickAdd: "Yeni müşteri adı:" },
  { name: "name", label: "İş Adı", required: true, full: true },
  { name: "status", label: "Durum", type: "select", options: JOB_STATUSES },
  { name: "address", label: "Adres", full: true },
  { name: "start_date", label: "İş Tarihi", type: "date" }, { name: "planned_end_date", label: "Planlanan Bitiş", type: "date" },
  { name: "contract_amount", label: "Sözleşme Tutarı (₺)", type: "number" }, { name: "actual_end_date", label: "Gerçek Bitiş", type: "date" },
  { name: "notes", label: "Not", type: "textarea" },
];

export default function Excavation() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [tick, setTick] = useState(0);
  const load = useCallback(() => {
    api.get("/excavation-jobs", { params: { q: q || undefined, status } }).then((r) => setData(r.data));
    setTick((t) => t + 1);
  }, [q, status]);
  const del = async (j) => {
    try { if (await confirmDelete(`/excavation-jobs/${j.id}`)) { toast.success("Kayıt silindi"); load(); } } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "name", label: "İş", render: (j) => <div><div className="font-medium">{j.name}</div><div className="text-xs text-muted-foreground">{j.address}</div></div> },
    { key: "plate", label: "Araç", className: "font-mono font-semibold" },
    { key: "customer_name", label: "İşi Kime Yaptık?" },
    { key: "status", label: "Durum", render: (j) => <StatusBadge value={j.status} /> },
    { key: "trip_count", label: "Sefer", align: "right", render: (j) => <b>{j.trip_count}</b> },
    { key: "vehicle_count", label: "Araç Sayısı", align: "right" },
    { key: "total_km", label: "Km", align: "right", render: (j) => j.total_km.toLocaleString("tr-TR") },
    { key: "contract_amount", label: "Sözleşme", align: "right", render: (j) => money(j.contract_amount) },
    { key: "total_cost", label: "Maliyet", align: "right", render: (j) => money(j.total_cost) },
    { key: "profit", label: "Kâr", align: "right", render: (j) => <b className={j.profit >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(j.profit)}</b> },
    { key: "x", label: "", render: (j) => can("hafriyat") && <span onClick={(e) => e.stopPropagation()}><Button size="sm" variant="ghost" className="text-rose-600"
      onClick={() => del(j)} data-testid={`delete-job-${j.id}`}><Trash2 className="w-4 h-4 mr-1" />Sil</Button></span> },
  ];
  return (
    <div className="space-y-6" data-testid="excavation-page">
      <PageHeader title="Hafriyat İşleri" subtitle="Araç odaklı: hangi araç, kime, hangi tarihte iş yaptı">
        {can("hafriyat") && (
          <FormDialog title="Yeni Hafriyat İşi" triggerLabel="Yeni İş" triggerTestId="add-job-btn" url="/excavation-jobs" onDone={load}
            successMsg="Hafriyat işi oluşturuldu" initial={{ status: "Aktif", start_date: todayStr(), contract_amount: 0 }}
            fields={JOB_FIELDS.filter((f) => f.name !== "actual_end_date")} />
        )}
      </PageHeader>
      <Tabs defaultValue="vehicles">
        <TabsList>
          <TabsTrigger value="vehicles" data-testid="tab-by-vehicle">Araçlara Göre</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-by-customer">Müşterilere Göre</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">İş Kayıtları</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicles"><ExcavationHistory by="vehicle" reloadKey={tick} /></TabsContent>
        <TabsContent value="customers"><ExcavationHistory by="customer" reloadKey={tick} /></TabsContent>
        <TabsContent value="jobs">
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="İş adı veya adres..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="job-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="md:w-48" data-testid="job-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm Durumlar</SelectItem>{JOB_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DataTable columns={columns} rows={data.items} onRowClick={(j) => navigate(`/hafriyat/${j.id}`)} testIdPrefix="job-row" />
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
